import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { oauth, TIMEZONE_SERVER_DATE } from "../utils";
import { isNoteComplete } from "./notes";

export const MY_TASKS = "your-tasks";
export const ASSIGNED_TASKS = "assigned-tasks";

/** Identifies one of the two task lists: your own reminders, or your team's. */
export type MyTasksListKey = typeof MY_TASKS | typeof ASSIGNED_TASKS;

/** Maps a list to the `type` value used by the /notifications-seen endpoint. */
export const NOTIFICATIONS_SEEN_TYPE_BY_LIST: Record<MyTasksListKey, "tasks" | "tasks_assigned"> = {
  [MY_TASKS]: "tasks",
  [ASSIGNED_TASKS]: "tasks_assigned",
};

// The dropdown only ever shows this many tasks; "View All Tasks (<total>)"
// appears once the server-reported total exceeds it.
export const TASKS_DISPLAY_LIMIT = 5;

// Assigned Tasks' completed reminders fetch (see fetchAssignedTasks below)
// asks for more than TASKS_DISPLAY_LIMIT: the panel drops any task that's
// complete but was already seen before (old, acknowledged business - see
// selectVisibleAssignedTasks), and without headroom, a handful of those
// among the most-recently-completed reminders could crowd out genuinely
// recent completions and make that section look empty.
const ASSIGNED_TASKS_COMPLETED_FETCH_LIMIT = TASKS_DISPLAY_LIMIT * 4;

export interface TaskNote {
  note_id: string;
  message_text: string;
  date_created: string;
  date_complete: string;
  reminder_complete: string | number;
  reminder_user: string;
  reminder_user_full_name?: string;
  reminder_user_image_paths?: { small?: string } | null;
  created_by: string;
  creator_user_full_name?: string;
  parent_type: string;
  parent_id: string;
  parent_name?: string;
  job_number?: string;
  job_name?: string;
}

/** Props shared by the per-tab section layouts (MyTasksSections, AssignedTasksSections). */
export interface TaskListSectionsProps {
  tab: MyTasksListKey;
  notes: TaskNote[];
  seenAt: string | null;
  onToggleComplete: (noteId: string, complete: boolean) => void;
}

interface MyTasksListState {
  notes: TaskNote[];
  total: number;
  seenAt: string | null;
  loading: boolean;
  // Whether the real (possibly capped) note list has ever been fetched for
  // this list - see selectRecentlyCompletedCount and
  // fetchAssignedTasksBadgeCount below.
  notesLoaded: boolean;
  // Recently-completed count reported by fetchAssignedTasksBadgeCount, a
  // lightweight server-side count used only until the real note list has
  // been fetched (notesLoaded); once it has, selectRecentlyCompletedCount
  // derives the count from `notes` instead, and this field is stale.
  recentlyCompletedCount: number;
}

export interface MyTasksState {
  lists: Record<MyTasksListKey, MyTasksListState>;
}

export type MyTasksRootState = {
  myTasks: MyTasksState;
  identity: any;
};

const emptyListState = (): MyTasksListState => ({
  notes: [],
  total: 0,
  seenAt: null,
  loading: false,
  notesLoaded: false,
  recentlyCompletedCount: 0,
});

/** Keeps the more recent of two seenAt values - see fetchMyTasksList.fulfilled. */
const resolveSeenAt = (prevSeenAt: string | null, seenAt: string | null): string | null =>
  prevSeenAt && (!seenAt || new Date(prevSeenAt).getTime() > new Date(seenAt).getTime()) ? prevSeenAt : seenAt;

const initialState: MyTasksState = {
  lists: {
    [MY_TASKS]: emptyListState(),
    [ASSIGNED_TASKS]: emptyListState(),
  },
};

async function fetchSeenAt(list: MyTasksListKey): Promise<string | null> {
  const { json } = await oauth("GET", "notifications-seen", {
    type: NOTIFICATIONS_SEEN_TYPE_BY_LIST[list],
  });
  return json?.[0]?.seen_at ?? null;
}

async function fetchTasks(params: Record<string, unknown>) {
  // No parent_type filter: matches the old reminders menu (createFetchReminders
  // in web/src/actions/header.js), which showed reminders across all parent
  // types (projects, clients, opportunities, orders, POs, etc.), not just JOB.
  const { json } = await oauth("INDEX", "note", {
    note_type: "ALL REMINDERS",
    include_complete: false,
    with_parent_data: true,
    with_po: true,
    order_by: "date_created",
    order_dir: "desc",
    max_results: TASKS_DISPLAY_LIMIT,
    ...params,
  });
  return {
    notes: (json?.notes ?? []) as TaskNote[],
    total: (json?.total ?? 0) as number,
  };
}

/**
 * ASSIGNED_TASKS: reminders I created for others (note_type=CREATED-REMINDERS
 * defaults to created_by=caller server-side) - both complete and incomplete,
 * so the panel can split them into "Recently Completed" / "In Progress".
 *
 * Fetched as two separate requests, not one `include_complete: true` request
 * ordered by date_complete - the endpoint applies max_results *after*
 * ordering, and with a single mixed request, enough recently-completed
 * reminders can push every still-incomplete one past the cutoff (incomplete
 * reminders all share the same never-completed zero date_complete, so they
 * always sort last in a DESC order). Splitting the fetch means each group is
 * paginated against its own cap, so neither can crowd the other out.
 */
async function fetchAssignedTasks() {
  const [incomplete, completed] = await Promise.all([
    fetchTasks({
      note_type: "CREATED-REMINDERS",
      include_complete: false,
      max_results: TASKS_DISPLAY_LIMIT,
      with_reminder_user_image: true,
    }),
    fetchTasks({
      note_type: "CREATED-REMINDERS",
      include_complete: true,
      max_results: ASSIGNED_TASKS_COMPLETED_FETCH_LIMIT,
      with_reminder_user_image: true,
      // Sort by date_complete, not the fetchTasks default of date_created:
      // completion recency (not creation recency) is what "Recently
      // Completed" is about. Incomplete reminders all share the same
      // never-completed zero-date value, so they tie and fall back to the
      // model's built-in date_created desc secondary sort - see
      // Notes::getBaseSelect. Without this, an old reminder completed today
      // could sort behind 20 more recently *created* reminders and never
      // surface here at all.
      order_by: "date_complete",
    }),
  ]);
  // include_complete: true has no complete-only mode server-side - it still
  // returns incomplete reminders too, just sorted last. Drop them: they're
  // already covered by the incomplete fetch above, and left in, they'd
  // render as duplicate rows.
  return {
    notes: [...incomplete.notes, ...completed.notes.filter(isNoteComplete)],
  };
}

export const fetchMyTasksList = createAsyncThunk(
  "myTasks/fetchList",
  async (list: MyTasksListKey, { getState }) => {
    const { identity } = getState() as MyTasksRootState;
    const userId = identity?.user_id;

    // identity isn't hydrated yet. Bail out to an empty result rather than
    // calling fetchTasks() with an undefined reminder_user_id, which the
    // endpoint would treat as "no user filter" and return every user's
    // reminders in the tenant.
    if (!userId) {
      return { list, notes: [], total: 0, seenAt: null };
    }

    if (list === MY_TASKS) {
      const [seenAt, { notes, total }] = await Promise.all([
        fetchSeenAt(list),
        fetchTasks({ reminder_user_id: userId }),
      ]);
      return { list, notes, total, seenAt };
    }

    const [seenAt, { notes }] = await Promise.all([fetchSeenAt(list), fetchAssignedTasks()]);
    // Assigned Tasks has no `total` (see fetchAssignedTasks) - left at the
    // same placeholder emptyListState() starts with, since it's never read.
    return { list, notes, total: 0, seenAt };
  },
);

/**
 * A cheap stand-in for fetchMyTasksList(ASSIGNED_TASKS), used only to get the
 * Assigned Tasks tab's badge count accurate before the dropdown is ever
 * opened (see TasksMenu's page-load effect) - asks the server for a count
 * instead of fetching and enriching the (capped) note rows just to derive one
 * client-side. Superseded the moment the real list is fetched - see
 * selectRecentlyCompletedCount.
 */
export const fetchAssignedTasksBadgeCount = createAsyncThunk(
  "myTasks/fetchAssignedTasksBadgeCount",
  async (_: void, { getState }) => {
    const { identity } = getState() as MyTasksRootState;
    if (!identity?.user_id) {
      return { count: 0, seenAt: null };
    }

    const seenAt = await fetchSeenAt(ASSIGNED_TASKS);
    const { json } = await oauth("INDEX", "note", {
      note_type: "CREATED-REMINDERS",
      completed_only: true,
      count_only: true,
      ...(seenAt ? { completed_after: seenAt } : {}),
    });
    return { count: (json?.total ?? 0) as number, seenAt };
  },
);

export const markMyTasksSeen = createAsyncThunk("myTasks/markSeen", async (list: MyTasksListKey) => {
  const { json } = await oauth("POST", "notifications-seen", {
    type: NOTIFICATIONS_SEEN_TYPE_BY_LIST[list],
  });
  return { list, seenAt: json?.seen_at ?? null };
});

export const toggleMyTaskComplete = createAsyncThunk(
  "myTasks/toggleComplete",
  async ({ list, noteId, complete }: { list: MyTasksListKey; noteId: string; complete: boolean }) => {
    // date_complete alongside reminder_complete, matching every other
    // reminder-toggle call site (e.g. ReminderDropdownMenu.js) — the server
    // doesn't derive one from the other.
    await oauth("PUT", `note/${noteId}`, {
      reminder_complete: complete ? "1" : "0",
      date_complete: complete ? moment().format("YYYY-MM-DD HH:mm:ss") : "0000-00-00 00:00:00",
    });
    return { list, noteId, complete };
  },
);

const slice = createSlice({
  name: "myTasks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyTasksList.pending, (state, action) => {
        state.lists[action.meta.arg].loading = true;
      })
      .addCase(fetchMyTasksList.fulfilled, (state, action) => {
        const { list, notes, total, seenAt } = action.payload;
        // Never let a fetch that was in flight while the list was marked
        // seen (markMyTasksSeen) regress seenAt back to the stale value it
        // read at request time — keep whichever is more recent.
        const resolvedSeenAt = resolveSeenAt(state.lists[list].seenAt, seenAt);
        state.lists[list] = {
          notes,
          total,
          seenAt: resolvedSeenAt,
          loading: false,
          notesLoaded: true,
          // Stale once notesLoaded is true - selectRecentlyCompletedCount
          // derives the real count from `notes` instead.
          recentlyCompletedCount: state.lists[list].recentlyCompletedCount,
        };
      })
      .addCase(fetchMyTasksList.rejected, (state, action) => {
        state.lists[action.meta.arg].loading = false;
        toast.error("Unable to load tasks.");
      })
      .addCase(fetchAssignedTasksBadgeCount.fulfilled, (state, action) => {
        const list = state.lists[ASSIGNED_TASKS];
        // The real list may have already been fetched (e.g. the dropdown was
        // opened before this lightweight request came back) - it's the more
        // accurate, live-updating source, so don't clobber it.
        if (list.notesLoaded) {
          return;
        }
        const resolvedSeenAt = resolveSeenAt(list.seenAt, action.payload.seenAt);
        if (resolvedSeenAt !== action.payload.seenAt) {
          return;
        }
        list.seenAt = resolvedSeenAt;
        list.recentlyCompletedCount = action.payload.count;
      })
      .addCase(markMyTasksSeen.fulfilled, (state, action) => {
        const list = state.lists[action.payload.list];
        list.seenAt = action.payload.seenAt;
        // Everything counted so far was completed before this new seenAt -
        // matches what selectRecentlyCompletedCount would derive from `notes`
        // for the same seenAt.
        list.recentlyCompletedCount = 0;
      })
      // Optimistic: flip the checkbox immediately and revert on failure. The
      // note is intentionally never removed from `notes` here — per spec it
      // stays visible (checked/unchecked) until the dropdown is reopened and
      // the list is refetched from the server.
      .addCase(toggleMyTaskComplete.pending, (state, action) => {
        const { list, noteId, complete } = action.meta.arg;
        const note = state.lists[list].notes.find((n) => n.note_id === noteId);
        if (note) {
          note.reminder_complete = complete ? "1" : "0";
        }
      })
      .addCase(toggleMyTaskComplete.rejected, (state, action) => {
        const { list, noteId, complete } = action.meta.arg;
        const note = state.lists[list].notes.find((n) => n.note_id === noteId);
        if (note) {
          note.reminder_complete = complete ? "0" : "1";
        }
        toast.error("Unable to update task.");
      });
  },
});

// note.date_created is a raw legacy MySQL "YYYY-MM-DD HH:MM:SS" string in the
// server's timezone, which `new Date()` doesn't reliably parse across
// browsers - parse it with moment.tz against TIMEZONE_SERVER_DATE instead of
// letting moment fall back to the browser's local timezone. seenAt is already
// ISO-8601 and doesn't need this - see isTaskNewlyPosted below.
export const MYSQL_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const toDate = (mysqlDateTime: string): Date =>
  moment.tz(mysqlDateTime, MYSQL_DATETIME_FORMAT, TIMEZONE_SERVER_DATE).toDate();

/** A task counts as "newly posted" if it was created after the list was last seen. */
export const isTaskNewlyPosted = (note: TaskNote, seenAt: string | null): boolean =>
  !seenAt || toDate(note.date_created).getTime() > new Date(seenAt).getTime();

/**
 * A task belongs in "Recently Completed" if it's done and was completed
 * after the list was last seen; otherwise (incomplete, or completed before
 * the list was last seen) it belongs in "In Progress".
 *
 * date_complete isn't guaranteed to be a real timestamp just because
 * reminder_complete is set: some call sites (e.g. Post.js's legacy
 * handleToggleReminder) flip reminder_complete without ever sending
 * date_complete, leaving it at the notes table's zero-date default, which
 * parses to an invalid Date. Treat that as "can't tell when it was
 * completed" rather than "just completed" - otherwise it renders as
 * "Invalid Date" (when seenAt is null) or silently disappears from both
 * sections (once seenAt is set, since an invalid date compares false either
 * way).
 */
export const isTaskRecentlyCompleted = (note: TaskNote, seenAt: string | null): boolean => {
  if (!isNoteComplete(note)) {
    return false;
  }
  const completedAt = toDate(note.date_complete).getTime();
  return !isNaN(completedAt) && (!seenAt || completedAt > new Date(seenAt).getTime());
};

export interface VisibleAssignedTasks {
  recentlyCompleted: TaskNote[];
  inProgress: TaskNote[];
}

/**
 * Splits Assigned Tasks notes into "Recently Completed" / "In Progress" for
 * display: a task that's complete but was already completed before the list
 * was last seen is old, acknowledged business - it's neither new nor active,
 * so it's dropped here rather than assigned to either group. Each group is
 * then capped at TASKS_DISPLAY_LIMIT independently, favoring the newest
 * completions first - a full "Recently Completed" section never shrinks how
 * many "In Progress" tasks show up, since the two aren't competing for the
 * same slots.
 *
 * Exported (rather than kept local to AssignedTasksSections) so
 * TasksMenuPanel can tell whether anything is left to show *after* the drop,
 * not just whether notes were fetched - both groups coming back empty is a
 * real possibility (e.g. every fetched note is complete-and-already-seen),
 * and the raw notes length alone can't tell the panel that.
 */
export const selectVisibleAssignedTasks = (notes: TaskNote[], seenAt: string | null): VisibleAssignedTasks => {
  const recentlyCompleted: TaskNote[] = [];
  const inProgress: TaskNote[] = [];
  notes.forEach((note) => {
    if (isTaskRecentlyCompleted(note, seenAt)) {
      recentlyCompleted.push(note);
    } else if (!isNoteComplete(note)) {
      inProgress.push(note);
    }
    // else: complete, but already seen - dropped, see comment above.
  });

  return {
    recentlyCompleted: recentlyCompleted.slice(0, TASKS_DISPLAY_LIMIT),
    inProgress: inProgress.slice(0, TASKS_DISPLAY_LIMIT),
  };
};

// Selectors
export const selectMyTasksList = (list: MyTasksListKey) => (state: MyTasksRootState) =>
  state.myTasks.lists[list];

export const selectNewlyPostedCount = (list: MyTasksListKey) => (state: MyTasksRootState) => {
  const { notes, seenAt } = state.myTasks.lists[list];
  return notes.filter((note) => isTaskNewlyPosted(note, seenAt)).length;
};

export const selectRecentlyCompletedCount = (list: MyTasksListKey) => (state: MyTasksRootState) => {
  const { notes, seenAt, notesLoaded, recentlyCompletedCount } = state.myTasks.lists[list];
  // Before the real (possibly capped) list has ever been fetched, fall back
  // to the lightweight server count from fetchAssignedTasksBadgeCount rather
  // than the empty `notes` array's (always-zero) filtered count.
  if (!notesLoaded) {
    return recentlyCompletedCount;
  }
  return notes.filter((note) => isTaskRecentlyCompleted(note, seenAt)).length;
};

export default slice;
